import { Component, OnInit, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material';
import { NavService } from 'src/app/nav.service';
import { ScheduleAgentsAIService } from 'src/app/scheduleAgents-ai.service';
import { getShiftPriorityOrder } from 'src/app/ShiftInfo';
import { ThemeService } from 'src/app/theme.service';

interface Agency {
  agencyId: number;
  agencyName: string;
}

export interface AgencyEmailData {
  positionDate: string;
  shift: string;
  agencyId: number;
  agencyName: string;
  startTime: string;
  checked?: boolean;
}

@Component({
  selector: 'app-agency-email-dialog',
  templateUrl: './agency-email-dialog.component.html',
  styleUrls: ['./agency-email-dialog.component.scss']
})

export class AgencyEmailDialogComponent implements OnInit {
  data: AgencyEmailData[] = [];
  selectedAgency: Agency = {
    agencyId: -1,
    agencyName: '',
  };
  agencyData: Agency[] = [];
  positionDatesAndData: { positionDate: string; dataSource: AgencyEmailData[] }[] = [
    { positionDate: new Date().toISOString().split('T')[0], dataSource: [] },
    { positionDate: new Date(new Date().setDate(new Date().getDate() + 1)).toISOString().split('T')[0], dataSource: [] },
    { positionDate: new Date(new Date().setDate(new Date().getDate() + 2)).toISOString().split('T')[0], dataSource: [] },
    { positionDate: new Date(new Date().setDate(new Date().getDate() + 3)).toISOString().split('T')[0], dataSource: [] },
    { positionDate: new Date(new Date().setDate(new Date().getDate() + 4)).toISOString().split('T')[0], dataSource: [] },
  ];
  isAnyChecked = false;
  isLoading = false;
  isSaving = false;

  constructor(
    public dialogRef: MatDialogRef<AgencyEmailDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public info: any,
    public dialog: MatDialog,
    private scheduleAgentsAIService: ScheduleAgentsAIService,
    private navService: NavService,
    public themeService: ThemeService,
  ) { }

  ngOnInit() {
    const dates = this.positionDatesAndData.map(pd => pd.positionDate);

    this.isLoading = true;
    this.navService.getAgencyScheduledForTodayAndTomorrow(dates).subscribe((data) => {
      this.data = data;

      const uniqueAgencies = Array.from(new Set(data.map(item => item.agencyId)))
        .map(id => {
          const agency = data.find(item => item.agencyId === id);
          return { agencyId: agency.agencyId, agencyName: agency.agencyName };
        });

      if (uniqueAgencies.length > 0) {
        this.selectedAgency = { agencyId: uniqueAgencies[0].agencyId, agencyName: uniqueAgencies[0].agencyName };
      }

      for (const item of data) {
        if (item.agencyId === this.selectedAgency.agencyId) {
          const positionDateIdx = this.positionDatesAndData.findIndex(pd => pd.positionDate === item.positionDate.substring(0, 10));
          if (positionDateIdx !== -1) {
            this.positionDatesAndData[positionDateIdx].dataSource.push(item);
          }
        }
      }

      this.agencyData = uniqueAgencies;

      this.positionDatesAndData.forEach(pd => {
        pd.dataSource.sort((a, b) => this.sortShifts(a.shift, b.shift));
      });

      this.isLoading = false;
    },
    (error) => {
      console.error('Error fetching agency data:', error);
      this.isLoading = false;
    });
  }

  sortShifts(a: string, b: string) {
    const priorityA = getShiftPriorityOrder(a);
    const priorityB = getShiftPriorityOrder(b);

    if (priorityA > priorityB) {
      return 1;
    }

    if (priorityA < priorityB) {
      return -1;
    }

    return 0;
  }

  closeClicked() {
    this.dialogRef.close({result: 'cancelled'});
  }

  agencyDropdownClicked(agencyId: number) {
    const agency = this.agencyData.find(a => a.agencyId === agencyId);
    if (agency) {
      this.selectedAgency = agency;
    }

    this.positionDatesAndData.forEach(pd => pd.dataSource = []);

    for (const item of this.data) {
      if (item.agencyId === this.selectedAgency.agencyId) {
        const positionDateIdx = this.positionDatesAndData.findIndex(pd => pd.positionDate === item.positionDate.substring(0, 10));
        if (positionDateIdx !== -1) {
          this.positionDatesAndData[positionDateIdx].dataSource.push(item);
        }
      }
    }

    this.positionDatesAndData.forEach(pd => {
      pd.dataSource.sort((a, b) => this.sortShifts(a.shift, b.shift));
    });

    this.isAnyChecked = this.positionDatesAndData.some(pd => pd.dataSource.some(item => item.checked));
  }

  handleCheckbox(event: any, element: AgencyEmailData) {
    const isChecked = event.target.checked;

    this.positionDatesAndData.forEach(pd => {
      if (pd.positionDate === element.positionDate.substring(0, 10)) {
        pd.dataSource = pd.dataSource.map(item => {
          if (item.positionDate.substring(0, 10) === element.positionDate.substring(0, 10) && item.shift === element.shift) {
            return { ...item, checked: isChecked };
          }
          return item;
        }
        );
      }
    });

    this.isAnyChecked = this.positionDatesAndData.some(pd => pd.dataSource.some(item => item.checked));
  }

  sendClicked() {
    const data = {
      agencyName: this.selectedAgency.agencyName,
      shifts: this.positionDatesAndData.reduce((acc, pd) => {
        const selectedShifts = pd.dataSource.filter(item => item.checked);
        const shiftStrings = selectedShifts.map(item => ({ shiftDate: pd.positionDate, shiftString: item.shift }));
        return [...acc, ...shiftStrings];
      }, [] as { shiftDate: string, shiftString: string }[]),
    };

    this.isSaving = true;

    this.scheduleAgentsAIService.sendAgencyEmailContent(data).subscribe(() => {
      this.isSaving = false;
      this.dialogRef.close({result: 'sent'});
    },
    (error) => {
      console.error('Error sending email content:', error);
      this.isSaving = false;
    });
  }
}
